import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import { Backup, CloudDownload, CloudUpload, Restore } from '@mui/icons-material'
import { backupService } from '../services/api'
import { ActivationBackupInfo, ActivationBackupRestoreResult } from '../types'
import { useStore } from '../store/authStore'
import { Navigate } from 'react-router-dom'

const unwrapData = <T,>(response: any): T => (response?.data ?? response) as T

export default function BackupPage() {
  const { user } = useStore()
  const [loading, setLoading] = useState(true)
  const [working, setWorking] = useState(false)
  const [latest, setLatest] = useState<ActivationBackupInfo | null>(null)
  const [feedback, setFeedback] = useState<{ severity: 'success' | 'error' | 'info'; message: string } | null>(null)
  const restoreInputRef = React.useRef<HTMLInputElement>(null)

  if (user?.role !== 'Owner') {
    return <Navigate to="/dashboard" replace />
  }

  const loadLatest = async () => {
    try {
      setLoading(true)
      const response = await backupService.getLatest()
      setLatest(unwrapData<ActivationBackupInfo | null>(response))
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'فشل تحميل معلومات النسخة الاحتياطية' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadLatest()
  }, [])

  const createBackup = async () => {
    try {
      setWorking(true)
      const response = await backupService.create()
      const result = unwrapData<{ backup: ActivationBackupInfo; message: string }>(response)
      setLatest(result?.backup ?? null)
      setFeedback({ severity: 'success', message: result?.message || 'تم إنشاء النسخة الاحتياطية بنجاح' })
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'فشل إنشاء النسخة الاحتياطية' })
    } finally {
      setWorking(false)
    }
  }

  const downloadBackup = () => {
    if (!latest?.fileName) return
    backupService.download(latest.fileName)
  }

  const restoreLatest = async () => {
    if (!latest?.fileName) return
    if (!window.confirm('سيتم استبدال قاعدة البيانات واتفاقيات الترخيص بالنسخة الاحتياطية. هل أنت متأكد؟')) return

    try {
      setWorking(true)
      const response = await backupService.restoreLatest()
      const result = unwrapData<ActivationBackupRestoreResult>(response)
      setFeedback({ severity: 'success', message: result?.message || 'تم الاسترجاع بنجاح' })
      await loadLatest()
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'فشل الاسترجاع' })
    } finally {
      setWorking(false)
    }
  }

  const restoreUpload = async (file: File) => {
    if (!window.confirm('سيتم استبدال قاعدة البيانات واتفاقيات الترخيص بالملف المرفوع. هل أنت متأكد؟')) return

    try {
      setWorking(true)
      const response = await backupService.restoreUpload(file)
      const result = unwrapData<ActivationBackupRestoreResult>(response)
      setFeedback({ severity: 'success', message: result?.message || 'تم الاسترجاع بنجاح' })
      await loadLatest()
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'فشل الاسترجاع' })
    } finally {
      setWorking(false)
      if (restoreInputRef.current) restoreInputRef.current.value = ''
    }
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
        <Backup color="primary" sx={{ fontSize: 36 }} />
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>النسخ الاحتياطي</Typography>
          <Typography color="text.secondary">للمالك فقط — قاعدة البيانات + اتفاقيات الترخيص PDF</Typography>
        </Box>
      </Stack>

      {feedback && (
        <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ mb: 3, borderRadius: 2 }}>
          {feedback.message}
        </Alert>
      )}

      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>آخر نسخة احتياطية</Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : !latest?.fileName ? (
            <Alert severity="info">لا توجد نسخة احتياطية بعد. أنشئ أول نسخة الآن.</Alert>
          ) : (
            <Stack spacing={1}>
              <Typography><strong>الملف:</strong> {latest.fileName}</Typography>
              <Typography><strong>بواسطة:</strong> {latest.createdByUsername || '—'}</Typography>
              <Typography><strong>التاريخ:</strong> {new Date(latest.createdAtLocal).toLocaleString('ar-IQ')}</Typography>
              <Typography><strong>الحجم:</strong> {latest.sizeMb} MB</Typography>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Button variant="contained" startIcon={<Backup />} onClick={createBackup} disabled={working}>
          {working ? 'جاري العمل...' : 'إنشاء نسخة احتياطية'}
        </Button>
        <Button variant="outlined" startIcon={<CloudDownload />} onClick={downloadBackup} disabled={!latest?.fileName || working}>
          تحميل النسخة
        </Button>
        <Button variant="outlined" color="warning" startIcon={<Restore />} onClick={restoreLatest} disabled={!latest?.fileName || working}>
          استرجاع آخر نسخة
        </Button>
        <Button
          variant="outlined"
          color="warning"
          startIcon={<CloudUpload />}
          disabled={working}
          onClick={() => restoreInputRef.current?.click()}
        >
          استرجاع من ملف
        </Button>
        <input
          ref={restoreInputRef}
          type="file"
          accept=".zip,application/zip"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) restoreUpload(file)
          }}
        />
      </Stack>

      <Alert severity="warning" sx={{ mt: 3, borderRadius: 2 }}>
        يُحتفظ بآخر نسخة احتياطية واحدة فقط على السيرفر. النسخة تشمل قاعدة PostgreSQL ومجلّد اتفاقيات الترخيص.
        يتطلّب pg_dump و pg_restore على السيرفر.
      </Alert>
    </Box>
  )
}
